import { FC, useEffect, useMemo, useState } from 'react';
import { Avatar, Flex, Layout, Menu, Result, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { fetchPatientById } from 'services/api/users';
import { PatientListRow } from 'types/api/users';

import { showErrorMessage } from 'utils/showErrorMessage';
import { PageWrapper } from 'components/ui';
import { DEFAULT_PAGE, SIDEBAR_OPTIONS } from './_constants';
import { StyledLayout } from './styled';

const { Content, Sider } = Layout;
const { Title, Text } = Typography;

/**
 * Patient detail — /access/patients/:id/:page. Mirrors the doctor profile
 * shell (sidebar menu + routed page), on the much smaller patient surface:
 * a read-only profile and the invoice history the BE exposes through
 * `GET /invoicing?patientId=`.
 */
const PatientProfile: FC = () => {
  const { id, page } = useParams();
  const navigate = useNavigate();

  const [patient, setPatient] = useState<PatientListRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchPatientById(id);
        if (!cancelled) setPatient(res);
      } catch (err) {
        if (!cancelled) {
          setNotFound(true);
          showErrorMessage(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const menuItems = useMemo(
    () =>
      SIDEBAR_OPTIONS.map(item => ({
        key: item.slug,
        label: (
          <Link to={`/access/patients/${id}/${item.slug}`}>{item.name}</Link>
        )
      })),
    [id]
  );

  const activePage = useMemo(
    () => SIDEBAR_OPTIONS.find(item => item.slug === page),
    [page]
  );

  const fullName = patient
    ? `${patient.first_name ?? ''} ${patient.last_name ?? ''}`.trim()
    : '';

  // An unknown slug would render an empty shell — send it to the first tab.
  if (!loading && !notFound && !activePage) {
    return <Navigate to={`/access/patients/${id}/${DEFAULT_PAGE}`} replace />;
  }

  const PageComponent = activePage?.component;

  return (
    <PageWrapper
      back
      loading={loading}
      onClickBack={() => navigate('/access/patients')}
      breadcrumbs={{
        items: [
          { title: 'Users' },
          { title: <Link to="/access/patients">Patients</Link> },
          { title: fullName || `#${id}` }
        ]
      }}
    >
      {notFound ? (
        <Result
          status="404"
          title="Patient not found"
          subTitle={`No patient with id ${id}.`}
        />
      ) : (
        <StyledLayout>
          <Sider width={300}>
            <Flex vertical align="center" gap={8} style={{ marginBottom: 32 }}>
              <Avatar
                size={88}
                src={patient?.avatar || undefined}
                icon={<UserOutlined />}
              />
              <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
                {fullName || '—'}
              </Title>
              <Text type="secondary">{patient?.email}</Text>
            </Flex>
            <Menu selectedKeys={[`${page}`]} items={menuItems} />
          </Sider>
          <Content>
            <Title level={3} style={{ marginTop: 0, marginBottom: 32 }}>
              {activePage?.name}
            </Title>
            {PageComponent && <PageComponent patient={patient} />}
          </Content>
        </StyledLayout>
      )}
    </PageWrapper>
  );
};

export default PatientProfile;
